/**
 * Call action hook — orchestrates permissions, token generation,
 * Convex presence tracking, and delegates to useAgoraCall for
 * the actual Agora SDK operations.
 *
 * Two flows depending on AV_CONFIG.USE_JOIN_SHEET:
 *
 * Legacy (true):  joinCall(mode) → token → join channel with 'off' (no permissions) → presence.
 *                 Permissions are only requested when user toggles mic/camera on in Call Controls.
 * New    (false): channel is auto-joined by AVCallProvider in receive-only mode.
 *                 toggleMute / toggleCamera request permissions on first enable
 *                 and write/clear presence when broadcasting starts/stops.
 */

import { useCallback, useRef } from 'react'
import { useAction, useMutation } from 'convex/react'
import { api } from '@ppwr/convex/_generated/api'
import type { Id } from '@ppwr/convex/_generated/dataModel'

import { useAVPermissions } from './useAVPermissions'
import type { AgoraCallActions, AgoraCallState } from './types'
import type { JoinMode } from './types'
import { AV_CONFIG } from './config'
import { rootStore } from '@/models'
import { events } from '@/models/analytics/analytics-events'

export type { JoinMode }

export function useCallActions(
  gameId: Id<'games'> | undefined,
  userId: string,
  agoraActions: AgoraCallActions,
  agoraState: AgoraCallState
) {
  const generateToken = useAction(api.av.actions.generateAgoraToken)
  const joinPresence = useMutation(api.av.presence.join)
  const leavePresence = useMutation(api.av.presence.leave)
  const { requestPermissions, openSettings } = useAVPermissions()
  const joiningRef = useRef(false)

  const ensurePermissions = useCallback(
    async (mode: JoinMode): Promise<boolean> => {
      if (mode === 'off') return true

      // Only request the permission for the feature being enabled (audio → mic only, camera → camera only).
      // Permissions are never requested on join; only when user toggles mic/camera on in Call Controls.
      const needsCamera = mode === 'video'
      const needsMic = mode === 'audio'
      const perms = await requestPermissions({
        camera: needsCamera,
        microphone: needsMic
      })
      if (needsMic && !perms.microphone) {
        rootStore?.analytics.setEvent(events.av.permissions_denied, {
          gameId, permission: 'microphone', requestedMode: mode, isConvexEnabled: true
        })
        openSettings()
        return false
      }
      if (needsCamera && !perms.camera) {
        rootStore?.analytics.setEvent(events.av.permissions_denied, {
          gameId, permission: 'camera', requestedMode: mode, isConvexEnabled: true
        })
        openSettings()
        return false
      }
      const permissionLabel = needsCamera && needsMic ? 'camera+microphone' : needsCamera ? 'camera' : 'microphone'
      rootStore?.analytics.setEvent(events.av.permissions_granted, {
        gameId, permission: permissionLabel, requestedMode: mode, isConvexEnabled: true
      })
      return true
    },
    [gameId, requestPermissions, openSettings]
  )

  // ─── Legacy flow (USE_JOIN_SHEET = true) ───────────────────────────
  // Join with 'off' so we never prompt on join; permissions only when user toggles mic/camera on.
  const joinCall = useCallback(
    async (_mode: JoinMode) => {
      if (!gameId || joiningRef.current) {
        return
      }
      joiningRef.current = true

      try {
        const { token, channelName, uid } = await generateToken({ gameId, userId })

        agoraActions.join(token, channelName, uid, 'off')

        await joinPresence({ gameId, userId })
      } catch (err) {
        console.error('[AV:joinCall] failed', err)
      } finally {
        joiningRef.current = false
      }
    },
    [gameId, userId, generateToken, agoraActions, joinPresence]
  )

  // ─── New flow helpers ──────────────────────────────────────────────

  const syncPresence = useCallback(
    async (willBePublishing: boolean) => {
      if (!gameId) return
      try {
        if (willBePublishing) {
          await joinPresence({ gameId, userId })
        } else {
          await leavePresence({ gameId, userId })
        }
      } catch {
        // best-effort
      }
    },
    [gameId, userId, joinPresence, leavePresence]
  )

  const toggleMute = useCallback(
    async (isMuted: boolean) => {
      const wantEnabled = !isMuted
      if (wantEnabled) {
        const ok = await ensurePermissions('audio')
        if (!ok) return
      }
      agoraActions.toggleMic(wantEnabled)

      rootStore?.analytics.setEvent(events.av.mic_toggled, {
        gameId, enabled: wantEnabled, isConvexEnabled: true
      })

      if (!AV_CONFIG.USE_JOIN_SHEET) {
        const willPublish = wantEnabled || agoraState.localCameraEnabled
        syncPresence(willPublish)
      }
    },
    [gameId, agoraActions, agoraState.localCameraEnabled, ensurePermissions, syncPresence]
  )

  const toggleCamera = useCallback(
    async (isCameraOn: boolean) => {
      if (isCameraOn) {
        const ok = await ensurePermissions('video')
        if (!ok) return
      }
      agoraActions.toggleCamera(isCameraOn)

      rootStore?.analytics.setEvent(events.av.camera_toggled, {
        gameId, enabled: isCameraOn, isConvexEnabled: true
      })

      if (!AV_CONFIG.USE_JOIN_SHEET) {
        const willPublish = isCameraOn || agoraState.localMicEnabled
        syncPresence(willPublish)
      }
    },
    [gameId, agoraActions, agoraState.localMicEnabled, ensurePermissions, syncPresence]
  )

  // ─── Leave ─────────────────────────────────────────────────────────

  const leaveCall = useCallback(async () => {
    if (AV_CONFIG.USE_JOIN_SHEET) {
      agoraActions.leave()
    } else {
      agoraActions.toggleMic(false)
      agoraActions.toggleCamera(false)
    }

    if (gameId) {
      try {
        await leavePresence({ gameId, userId })
      } catch {
        // best-effort cleanup
      }
    }
  }, [agoraActions, gameId, userId, leavePresence])

  return { joinCall, toggleMute, toggleCamera, leaveCall }
}
